import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon, DocumentDuplicateIcon, MagnifyingGlassIcon, QuestionMarkCircleIcon, WalletIcon } from "@heroicons/react/24/outline";
import Header from "../components/Header";
import Get from "../services/Get";
import Post from "../services/Post";
import { useFaqs } from "../components/SiteConfigContext";
import { apiError, fetchAllPages, isApiFailure, marketAssetsToCatalog, unwrapList } from "../services/providerData";
import "./DepositCrypto.css";
import "./WithdrawCrypto.css";

const symbolOf = (asset) => String(asset?.symbol ?? asset?.coin ?? asset?.asset ?? asset?.currency ?? asset?.coin_name ?? "").toUpperCase();
const nameOf = (asset) => asset?.name ?? asset?.coinName ?? asset?.coin_name ?? asset?.full_name ?? symbolOf(asset);
const networkOf = (network) => network?.network ?? network?.network_name ?? network?.chain ?? network?.name ?? network?.networkName ?? network?.network_code ?? "";
const networkCoin = (network) => String(network?.coin ?? network?.asset ?? network?.symbol ?? network?.currency ?? network?.coin_code ?? "").toUpperCase();
const logoOf = (asset) => {
  const value = asset?.logo_url ?? asset?.logoUrl ?? asset?.logo_uri ?? asset?.icon_url ?? asset?.iconUrl ?? asset?.image_url ?? asset?.imageUrl ?? asset?.coin_icon ?? asset?.coinIcon ?? asset?.image ?? asset?.logo ?? asset?.icon;
  return (typeof value === "string" ? value : value?.url ?? value?.src) || `https://assets.coincap.io/assets/icons/${symbolOf(asset).toLowerCase()}@2x.png`;
};
const monogramOf = (asset) => symbolOf(asset).replace(/^\d+/, "").slice(0, 2) || "¤";
const priorityOf = (asset) => ({ BTC: 0, USDT: 1, BNB: 2, ETH: 3, USDC: 4, SOL: 5 }[symbolOf(asset)] ?? 100);
const prettyNetwork = (name) => ({ BSC: "BNB Smart Chain (BSC)", SOL: "Solana (SOL)", ETH: "Ethereum (ETH)", BTC: "Bitcoin (BTC)", TRX: "TRON (TRX)" }[String(name).toUpperCase()] ?? name);
const enabled = (value) => value === true || value === 1 || value === "1" || value === "true";
const loadWalletBalances = async () => {
  const response = await Get.Wallets({ limit: 500 });
  if (isApiFailure(response) || response?.status === false) throw new Error(apiError(response));
  return Array.isArray(response?.balances) ? response.balances : [];
};

function CoinMark({ asset }) {
  const [failed, setFailed] = useState(false);
  const image = logoOf(asset);
  return image && !failed
    ? <img className="coin-mark coin-logo" src={image} alt="" loading="lazy" onError={() => setFailed(true)} />
    : <span className="coin-mark" style={{ "--coin-color": asset?.color ?? "#8b93a1" }}>{monogramOf(asset)}</span>;
}

const defaultWithdrawalFaqs = [
  "How to withdraw crypto? (Video)",
  "How to find my transaction ID (TxID)?",
  "How to recover my BEP-20 tokens?",
  "Deposit & withdrawal status query",
  "How to complete identity verification?",
].map((question) => ({ question }));

function WithdrawCrypto() {
  const withdrawalFaqs = useFaqs("withdraw", defaultWithdrawalFaqs);
  const [assets, setAssets] = useState([]);
  const [assetQuery, setAssetQuery] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetMenuOpen, setAssetMenuOpen] = useState(false);
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [networkMenuOpen, setNetworkMenuOpen] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState("Address");
  const [localAssets, setLocalAssets] = useState([]);
  const [localNetworks, setLocalNetworks] = useState([]);
  const [walletBalances, setWalletBalances] = useState([]);
  const [walletMetaError, setWalletMetaError] = useState("");
  const [address, setAddress] = useState("");
  const [memo, setMemo] = useState("");
  const [amount, setAmount] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [networkError, setNetworkError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [recipientTillionxId, setRecipientTillionxId] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");
  const [recentType, setRecentType] = useState("Address");
  const [recentWithdrawals, setRecentWithdrawals] = useState([]);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [hideWithdrawalErrors, setHideWithdrawalErrors] = useState(true);

  useEffect(() => {
    let active = true;
    fetchAllPages(Get.ProviderCatalog, "assets")
      .then(async (result) => {
        if (result.items.length === 0 && !isApiFailure(result.response)) {
          const market = await Get.MarketAssets().catch(() => null);
          if (market && !isApiFailure(market)) result = { ...result, items: marketAssetsToCatalog(unwrapList(market, "assets")) };
        }
        if (!active) return;
        const { response, items } = result;
        if (isApiFailure(response)) setCatalogError(apiError(response));
        else {
          const sortedAssets = items.sort((left, right) => priorityOf(left) - priorityOf(right) || symbolOf(left).localeCompare(symbolOf(right)));
          setAssets(sortedAssets);
          setSelectedAsset((current) => current ?? sortedAssets.find((asset) => symbolOf(asset) === "BTC") ?? null);
        }
      })
      .catch(() => { if (active) setCatalogError("Couldn't load assets. Please sign in and try again."); })
      .finally(() => { if (active) setCatalogLoading(false); });

    Get.CustodyTransactions({ type: "withdrawal", per_page: 10 })
      .then((response) => {
        if (!active || isApiFailure(response)) return;
        const rows = unwrapList(response, "transactions");
        setRecentWithdrawals(rows.filter((row) => /withdraw/i.test(row.type ?? row.transaction_type ?? row.direction ?? "withdrawal")).slice(0, 10));
      })
      .catch(() => {});

    Get.InternalTransfers({ per_page: 10 })
      .then((response) => {
        if (!active || isApiFailure(response)) return;
        setRecentTransfers(unwrapList(response, "transfers"));
      })
      .catch(() => {});

    loadWalletBalances()
      .then((balances) => { if (active) setWalletBalances(balances); })
      .catch(() => {});

    Get.WalletMeta()
      .then((response) => {
        if (!active) return;
        if (isApiFailure(response) || response?.status === false) {
          setWalletMetaError(apiError(response));
          return;
        }
        setLocalAssets(unwrapList(response, "assets"));
        setLocalNetworks(unwrapList(response, "networks"));
      })
      .catch(() => { if (active) setWalletMetaError("Couldn't load local asset and network IDs for withdrawals."); });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedAsset) {
      setNetworks([]);
      setNetworkLoading(false);
      setNetworkError("");
      return undefined;
    }
    if (selectedAsset.fromMarket) {
      setNetworks([]);
      setSelectedNetwork(null);
      setNetworkError("");
      setNetworkLoading(false);
      return undefined;
    }

    let active = true;
    setNetworks([]);
    setSelectedNetwork(null);
    setNetworkError("");
    setNetworkLoading(true);
    // The API's enabled_only query validation is inconsistent across environments;
    // request the asset's networks and filter withdraw-enabled networks here.
    Get.ProviderNetworks({ coin: symbolOf(selectedAsset) })
      .then((response) => {
        if (!active) return;
        if (isApiFailure(response)) {
          setNetworkError(apiError(response));
          return;
        }
        const rows = unwrapList(response, "networks").filter((network) => enabled(network.withdraw_enabled));
        setNetworks(rows);
      })
      .catch(() => { if (active) setNetworkError("Couldn't load withdrawal networks for this asset."); })
      .finally(() => { if (active) setNetworkLoading(false); });
    return () => { active = false; };
  }, [selectedAsset]);

  const heldBySymbol = useMemo(() => walletBalances.reduce((held, row) => {
    const symbol = symbolOf(row.asset);
    const amount = Number(row.available ?? row.balance ?? 0) || 0;
    return symbol && amount > 0 ? { ...held, [symbol]: (held[symbol] ?? 0) + amount } : held;
  }, {}), [walletBalances]);
  // Zero-balance coins are hidden once balances are known; with no funded coin at all the full list is kept.
  const filteredAssets = useMemo(() => {
    const funded = Object.keys(heldBySymbol).length > 0;
    return assets
      .filter((asset) => !funded || heldBySymbol[symbolOf(asset)] > 0)
      .filter((asset) => `${symbolOf(asset)} ${nameOf(asset)}`.toLowerCase().includes(assetQuery.toLowerCase()))
      .sort((left, right) => (heldBySymbol[symbolOf(right)] ?? 0) - (heldBySymbol[symbolOf(left)] ?? 0));
  }, [assets, assetQuery, heldBySymbol]);
  const visibleNetworks = useMemo(() => {
    const catalogNetworks = selectedAsset?.networks ?? selectedAsset?.network_list ?? selectedAsset?.networkList ?? [];
    const available = networks.length ? networks : catalogNetworks.filter((network) => enabled(network.withdraw_enabled));
    const catalogByName = new Map(catalogNetworks.map((network) => [networkOf(network).toUpperCase(), network]));
    return available
      .filter((network) => !networkCoin(network) || networkCoin(network) === symbolOf(selectedAsset))
      .map((network) => ({ ...network, ...(catalogByName.get(networkOf(network).toUpperCase()) ?? {}) }));
  }, [networks, selectedAsset]);
  const localNetwork = selectedNetwork && localNetworks.find((network) => String(network.symbol ?? "").toUpperCase() === networkOf(selectedNetwork).toUpperCase());
  const localAsset = selectedAsset && localNetwork && localAssets.find((asset) =>
    String(asset.symbol ?? "").toUpperCase() === symbolOf(selectedAsset) && Number(asset.network_id) === Number(localNetwork.id)
  );
  const withdrawalBalance = localAsset && walletBalances.find((balance) => Number(balance.asset_id) === Number(localAsset.id));
  const transferBalance = selectedAsset && walletBalances
    .filter((balance) => symbolOf(balance.asset) === symbolOf(selectedAsset))
    .sort((left, right) => Number(right.available ?? right.balance ?? 0) - Number(left.available ?? left.balance ?? 0))[0];
  const transferAssetId = transferBalance?.asset_id ?? transferBalance?.asset?.id
    ?? localAssets.find((asset) => String(asset.symbol ?? "").toUpperCase() === symbolOf(selectedAsset))?.id;
  const availableBalance = withdrawMethod === "Address"
    ? withdrawalBalance?.available
    : transferBalance?.available ?? transferBalance?.balance;
  const amountExceedsAvailable = amount !== "" && availableBalance != null && Number(amount) > Number(availableBalance);
  const transferReady = withdrawMethod === "Address"
    ? Boolean(selectedNetwork && address.trim())
    : Boolean(selectedAsset && recipientTillionxId.trim());
  const visibleWithdrawals = hideWithdrawalErrors
    ? recentWithdrawals.filter((row) => !/failed|rejected|error/i.test(String(row.status ?? "")))
    : recentWithdrawals;
  const visibleTransfers = hideWithdrawalErrors
    ? recentTransfers.filter((row) => !/failed|rejected|error/i.test(String(row.status ?? "")))
    : recentTransfers;

  const chooseAsset = (asset) => {
    setSelectedAsset(asset);
    setSelectedNetwork(null);
    setAssetMenuOpen(false);
    setNetworkMenuOpen(false);
    setAddress("");
    setMemo("");
    setAmount("");
    setRecipientTillionxId("");
    setSubmitError("");
    setSubmitSuccess("");
    setTransferError("");
    setTransferSuccess("");
  };

  const submitWithdrawal = async (event) => {
    event.preventDefault();
    if (!selectedAsset || !selectedNetwork || !address.trim() || !Number(amount) || Number(amount) <= 0) return;
    if (!localAsset || !localNetwork) {
      setSubmitError(walletMetaError || "This asset and network are not enabled in wallet metadata yet.");
      return;
    }
    setSubmitLoading(true);
    setSubmitError("");
    setSubmitSuccess("");
    try {
      const response = await Post.RequestWithdrawal({
        asset_id: localAsset.id,
        network_id: localNetwork.id,
        address: address.trim(),
        amount: String(amount),
        address_tag: selectedNetwork.memo_required ? memo.trim() || null : null,
        idempotency_key: window.crypto?.randomUUID?.() ?? `withdraw-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      if (isApiFailure(response)) setSubmitError(apiError(response));
      else {
        setSubmitSuccess(response?.message ?? "Withdrawal request submitted successfully.");
        const transaction = response?.transaction ?? response?.data?.transaction;
        if (transaction) setRecentWithdrawals((current) => [{
          ...transaction,
          asset: transaction.asset ?? { symbol: symbolOf(selectedAsset) },
          network: transaction.network ?? { symbol: networkOf(selectedNetwork) },
        }, ...current].slice(0, 10));
        setAmount("");
        loadWalletBalances().then(setWalletBalances).catch(() => {});
      }
    } catch {
      setSubmitError("Couldn't submit withdrawal. Please sign in and try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const submitInternalTransfer = async (event) => {
    event.preventDefault();
    if (!selectedAsset || !recipientTillionxId.trim() || !Number(amount) || Number(amount) <= 0) return;
    if (!transferAssetId) {
      setTransferError(walletMetaError || "This asset is not available in your wallet yet.");
      return;
    }
    setTransferLoading(true);
    setTransferError("");
    setTransferSuccess("");
    try {
      const response = await Post.CreateInternalTransfer({
        recipient_id: recipientTillionxId.trim(),
        asset_id: transferAssetId,
        amount: String(amount),
        idempotency_key: window.crypto?.randomUUID?.() ?? `transfer-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      if (isApiFailure(response)) {
        setTransferError(apiError(response));
      } else {
        setTransferSuccess(response?.message ?? "Transfer completed successfully.");
        const transfer = response?.transfer ?? response?.data?.transfer;
        if (transfer) setRecentTransfers((current) => [transfer, ...current].slice(0, 10));
        setAmount("");
        loadWalletBalances().then(setWalletBalances).catch(() => {});
      }
    } catch {
      setTransferError("Couldn't complete the transfer. Please try again.");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="deposit-page">
      <Header />
      <div className="deposit-layout">
        <aside className="deposit-sidebar" aria-label="Wallet actions">
          <Link className="deposit-side-link" to="/depositCrypto"><ArrowDownTrayIcon />Deposit Asset</Link>
          <Link className="deposit-side-link active" to="/withdrawCrypto"><ArrowUpTrayIcon />Withdraw Asset</Link>
          <button className="deposit-side-link" type="button"><WalletIcon />Deposit Fiat<ChevronRightIcon className="side-chevron" /></button>
          <button className="deposit-side-link" type="button"><WalletIcon />Withdraw Fiat<ChevronRightIcon className="side-chevron" /></button>
        </aside>

        <main className="deposit-main">
          <div className="deposit-content-grid withdraw-content-grid">
            <section className="deposit-steps" aria-label="Withdrawal steps">
              <div className={`deposit-step ${selectedAsset ? "is-complete" : "is-active"}`}>
                <span className="step-marker">1</span>
                <div className="step-body">
                  <h2>Select Asset</h2>
                  <div className="asset-picker">
                    <button className={`asset-select-trigger ${assetMenuOpen ? "is-open" : ""}`} type="button" aria-expanded={assetMenuOpen} onClick={() => setAssetMenuOpen(!assetMenuOpen)}>
                      {selectedAsset ? <><CoinMark asset={selectedAsset} /><strong>{symbolOf(selectedAsset)}</strong><span>{nameOf(selectedAsset)}</span></> : <><MagnifyingGlassIcon /><span>{catalogLoading ? "Loading assets…" : "Select Asset"}</span></>}
                      <ChevronDownIcon />
                    </button>
                    {assetMenuOpen && <div className="asset-select-popover">
                      <label className="asset-search"><MagnifyingGlassIcon /><input autoFocus value={assetQuery} onChange={(event) => setAssetQuery(event.target.value)} placeholder="Search" aria-label="Search assets" /></label>
                      <span className="popover-caption">Coin List</span>
                      <div className="asset-options">
                        {catalogLoading && <span className="asset-empty">Loading available assets…</span>}
                        {!catalogLoading && filteredAssets.map((asset, index) => <button key={`${symbolOf(asset)}-${index}`} type="button" title={`${nameOf(asset)} (${symbolOf(asset)})`} onClick={() => chooseAsset(asset)} className={`asset-option ${selectedAsset === asset ? "selected" : ""}`}><CoinMark asset={asset} /><strong>{symbolOf(asset)}</strong><span>{nameOf(asset)}</span>{heldBySymbol[symbolOf(asset)] > 0 && <em className="asset-balance">{heldBySymbol[symbolOf(asset)]}</em>}</button>)}
                        {!catalogLoading && !filteredAssets.length && <span className="asset-empty">{catalogError || "No available assets found"}</span>}
                      </div>
                    </div>}
                    {catalogError && <p className="api-inline-error">{catalogError}</p>}
                  </div>
                </div>
              </div>

              <div className={`deposit-step ${selectedAsset ? "is-active" : "is-disabled"}`}>
                <span className="step-marker">2</span>
                <div className="step-body">
                  <h2>Withdraw to</h2>
                  <div className="withdraw-method-tabs" role="tablist" aria-label="Withdrawal destination">
                    {['Address', 'Tillionx user'].map((method) => <button key={method} type="button" role="tab" aria-selected={withdrawMethod === method} className={withdrawMethod === method ? "active" : ""} onClick={() => { setWithdrawMethod(method); setSelectedNetwork(null); setSubmitError(""); setSubmitSuccess(""); }}>{method}</button>)}
                  </div>
                  {selectedAsset && withdrawMethod === "Address" && <div className="withdraw-address-field">
                    <label className="visually-hidden" htmlFor="withdraw-address">Withdrawal address</label>
                    <div className="withdraw-address-input"><input id="withdraw-address" value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Enter Address" autoComplete="off" /><button type="button" aria-label="Paste withdrawal address" onClick={async () => { try { setAddress(await navigator.clipboard.readText()); } catch { setSubmitError("Clipboard access is unavailable. Paste the address into the field."); } }}><DocumentDuplicateIcon /></button></div>
                    <div className="network-picker">
                      <button className="network-select-trigger" type="button" disabled={!visibleNetworks.length} aria-expanded={networkMenuOpen} onClick={() => setNetworkMenuOpen(!networkMenuOpen)}>
                        <span>{selectedNetwork ? networkOf(selectedNetwork) : networkLoading ? "Loading networks…" : "Select network"}</span><ChevronDownIcon />
                      </button>
                      {networkMenuOpen && <div className="network-select-popover">
                        <p className="network-notice">Only choose a network supported by the destination address.</p>
                        {visibleNetworks.map((network, index) => <button type="button" key={`${networkOf(network)}-${index}`} onClick={() => { setSelectedNetwork(network); setNetworkMenuOpen(false); setSubmitError(""); setSubmitSuccess(""); }} className={`network-option ${selectedNetwork === network ? "selected" : ""}`}><span><strong>{networkOf(network)}</strong><small>{prettyNetwork(networkOf(network))}</small></span><small>{network.withdraw_fee ?? network.fee ? `Fee ${network.withdraw_fee ?? network.fee} ${symbolOf(selectedAsset)}` : "Withdrawal network"}</small></button>)}
                      </div>}
                    </div>
                    {networkError && <p className="api-inline-error">{networkError}</p>}
                    {selectedNetwork?.memo_required && <><label htmlFor="withdraw-memo">Memo / Tag</label><input id="withdraw-memo" value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="Enter memo or tag" /></>}
                  </div>}
                  {selectedAsset && withdrawMethod === "Tillionx user" && <div className="transfer-recipient-field">
                    <label htmlFor="tillionx-recipient-id">Recipient</label>
                    <input id="tillionx-recipient-id" value={recipientTillionxId} onChange={(event) => setRecipientTillionxId(event.target.value)} placeholder="TillionX ID, email, or mobile number" autoComplete="off" />
                    <small>Transfers are instant and can’t be reversed. Double-check the recipient details.</small>
                  </div>}
                  {!selectedAsset && <p className="step-hint">Select an asset to see available withdrawal networks.</p>}
                </div>
              </div>

              <div className={`deposit-step ${transferReady ? "is-active" : "is-disabled"}`}>
                <span className="step-marker">3</span>
                <div className="step-body">
                  <h2>{withdrawMethod === "Address" ? "Withdraw amount" : "Transfer amount"}</h2>
                  {transferReady ? <form className="withdraw-amount-form" onSubmit={withdrawMethod === "Address" ? submitWithdrawal : submitInternalTransfer}>
                    <label htmlFor="withdraw-amount">Amount</label>
                    <div className="withdraw-amount-input"><input id="withdraw-amount" inputMode="decimal" type="number" min="0" step="any" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="Minimum withdrawal amount" /><span>{symbolOf(selectedAsset)}</span></div>
                    {availableBalance != null && <small>Available balance: {availableBalance} {symbolOf(selectedAsset)}</small>}
                    {amountExceedsAvailable && <p className="api-inline-error">Amount exceeds your available balance.</p>}
                    {withdrawMethod === "Address" && (selectedNetwork.withdraw_min ?? selectedNetwork.withdraw_minimum) != null && <small>Minimum withdrawal: {selectedNetwork.withdraw_min ?? selectedNetwork.withdraw_minimum} {symbolOf(selectedAsset)}</small>}
                    {withdrawMethod === "Address" && (selectedNetwork.withdraw_fee ?? selectedNetwork.fee) != null && <small>Network fee: {selectedNetwork.withdraw_fee ?? selectedNetwork.fee} {symbolOf(selectedAsset)}</small>}
                    {withdrawMethod === "Address" && submitError && <p className="api-inline-error">{submitError}</p>}{withdrawMethod === "Address" && submitSuccess && <p className="withdraw-success">{submitSuccess}</p>}
                    {withdrawMethod === "Tillionx user" && transferError && <p className="api-inline-error">{transferError}</p>}{withdrawMethod === "Tillionx user" && transferSuccess && <p className="withdraw-success">{transferSuccess}</p>}
                    <button className="withdraw-submit-button" type="submit" disabled={(withdrawMethod === "Address" ? submitLoading : transferLoading) || !Number(amount) || Number(amount) <= 0 || amountExceedsAvailable}>{(withdrawMethod === "Address" ? submitLoading : transferLoading) ? "Submitting…" : withdrawMethod === "Address" ? "Withdraw" : "Transfer"}</button>
                  </form> : <p className="step-hint">{withdrawMethod === "Address" ? "Select a network and enter a withdrawal address to continue." : "Enter a TillionX ID to continue."}</p>}
                </div>
              </div>
            </section>

            <aside className="deposit-faq withdraw-faq">
              <div className="withdraw-faq-heading"><h2>FAQ</h2><Link to="/support-center">More <ChevronRightIcon /></Link></div>
              {withdrawalFaqs.map((faq) => <button type="button" className="faq-link" key={faq.question}>{faq.question}<ChevronRightIcon /></button>)}
              <Link className="faq-support" to="/support-center"><QuestionMarkCircleIcon />Need help? Visit Support Center</Link>
            </aside>
          </div>

          <section className="recent-deposits">
            <div className="recent-heading"><div><h2>Recent Withdrawals</h2><span><ClockIcon />Your latest withdrawal activity</span></div><div className="withdrawal-history-actions"><label><input type="checkbox" checked={hideWithdrawalErrors} onChange={(event) => setHideWithdrawalErrors(event.target.checked)} />Hide error notices</label><Link to="/transaction-history">More <ChevronRightIcon /></Link></div></div>
            <div className="recent-tabs"><button type="button" onClick={() => setRecentType("Address")} className={recentType === "Address" ? "active" : ""}>Address</button><button type="button" onClick={() => setRecentType("Tillionx user")} className={recentType === "Tillionx user" ? "active" : ""}>Tillionx user</button></div>
            {recentType === "Address" && visibleWithdrawals.length > 0 ? <div className="withdrawal-records">{visibleWithdrawals.map((row, index) => <div className="withdrawal-record" key={row.id ?? row.uuid ?? index}><strong>{row.asset?.symbol ?? row.coin ?? (typeof row.asset === "string" ? row.asset : "—")}</strong><span>{row.amount ?? "—"}</span><span>{row.network?.symbol ?? row.network?.name ?? "—"}</span><span>{row.address ?? row.to_address ?? "—"}</span><span>{row.status ?? "Pending"}</span></div>)}</div>
              : recentType === "Tillionx user" && visibleTransfers.length > 0 ? <div className="withdrawal-records">{visibleTransfers.map((row, index) => { const peer = row.direction === "received" ? row.sender : row.recipient; return <div className="withdrawal-record" key={row.id ?? index}><strong>{row.asset?.symbol ?? symbolOf(selectedAsset) ?? "—"}</strong><span>{row.direction === "received" ? "Received" : "Sent"}</span><span>{peer?.tillionx_id ?? "TillionX user"}</span><span>{row.amount ?? "—"}</span><span>{row.status ?? "Completed"}</span></div>; })}</div>
              : <div className="deposit-empty"><div className="empty-icon"><ArrowUpTrayIcon /></div><strong>{recentType === "Address" ? "No recent withdrawal records" : "No recent TillionX transfers"}</strong><span>{recentType === "Address" ? "Your withdrawal history will show here." : "Transfers you send or receive will show here."}</span></div>}
          </section>
        </main>
      </div>
      <div className="deposit-legal-footer"><span>Tillionx© 2026</span><button type="button">Cookie Preferences</button></div>
    </div>
  );
}

export default WithdrawCrypto;
